import { useIntl } from 'react-intl';
import { Table } from '../../../../ui';
import { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  DeliveryConditionsData,
  ListingProduct,
  DeliveryProductReference,
} from '../../../../types/ListingData';

interface DeliveryConditionsTabProps {
  deliveryConditions: DeliveryConditionsData[];
}

type DeliveryProduct = ListingProduct | DeliveryProductReference;

export const DeliveryConditionsTab = ({
  deliveryConditions,
}: DeliveryConditionsTabProps) => {
  const intl = useIntl();

  if (!deliveryConditions || deliveryConditions.length === 0) {
    return (
      <div>{intl.formatMessage({ id: 'listing.no_delivery_conditions' })}</div>
    );
  }

  const columns: ColumnsType<DeliveryProduct> = [
    {
      title: intl.formatMessage({ id: 'listing.product_category' }),
      key: 'category_name',
      render: (_, record) => {
        const product = record as {
          listing_product?: { name?: string; category?: { name?: string } };
        };
        return (
          product.listing_product?.name ||
          product.listing_product?.category?.name ||
          '-'
        );
      },
    },
    {
      title: intl.formatMessage({ id: 'listing.delivery_quantity' }),
      key: 'delivery_quantity',
      render: (_, record) => {
        const product = record as { delivery_quantity?: number | string };
        return product.delivery_quantity || '-';
      },
    },
  ];

  const formatDateRange = (startDate?: string, endDate?: string) => {
    if (!startDate || !endDate) return null;

    return `${dayjs(startDate).format('DD.MM.YYYY')} - ${dayjs(endDate).format('DD.MM.YYYY')}`;
  };

  return (
    <div>
      {deliveryConditions.map((condition, index) => {
        const isDetailedDelivery =
          condition.delivery_type === 'detailed_delivery';
        const dateRange = formatDateRange(
          isDetailedDelivery
            ? condition.delivery_start_date
            : condition.contract_start_date,
          isDetailedDelivery
            ? condition.delivery_end_date
            : condition.contract_end_date
        );

        return (
          <div key={condition.id} className="delivery-condition-section">
            {index > 0 && <div className="delivery-divider" />}

            {condition.contract_period_type && (
              <div className="delivery-period-type">
                <strong>
                  {intl.formatMessage({ id: 'listing.contract_period_type' })}:
                </strong>{' '}
                {intl.formatMessage({
                  id: `listing.contract_period_type.${condition.contract_period_type}`,
                })}
              </div>
            )}

            {dateRange && (
              <div className="delivery-date-range">
                <strong>
                  {intl.formatMessage({ id: 'listing.delivery_period' })}:
                </strong>{' '}
                {dateRange}
              </div>
            )}

            {condition.delivery_period_value &&
              condition.delivery_period_unit && (
                <div className="delivery-period-duration">
                  <strong>
                    {intl.formatMessage({
                      id: 'listing.delivery_period_duration',
                    })}
                    :
                  </strong>{' '}
                  {condition.delivery_period_value}{' '}
                  {intl.formatMessage({
                    id: `listing.period_unit.${condition.delivery_period_unit}`,
                  })}
                </div>
              )}

            {condition.delivery_frequency_value &&
              condition.delivery_frequency_unit && (
                <div className="delivery-frequency">
                  <strong>
                    {intl.formatMessage({ id: 'listing.delivery_frequency' })}:
                  </strong>{' '}
                  {condition.delivery_frequency_value}{' '}
                  {intl.formatMessage({
                    id: `listing.frequency_unit.${condition.delivery_frequency_unit}`,
                  })}
                </div>
              )}

            {condition.delivery_address && (
              <div className="delivery-address">
                <strong>
                  {intl.formatMessage({ id: 'listing.delivery_address' })}:
                </strong>{' '}
                {condition.delivery_address}
              </div>
            )}

            {condition.free_form_description && (
              <div className="delivery-description">
                <strong>
                  {intl.formatMessage({ id: 'listing.delivery_description' })}:
                </strong>{' '}
                {condition.free_form_description}
              </div>
            )}

            {!!condition.products?.length && (
              <Table
                columns={columns}
                dataSource={condition.products}
                disablePagination={true}
                rowKey="id"
              />
            )}
          </div>
        );
      })}
    </div>
  );
};
